/**
 * The organisation every screen is acting in, and what this person may do
 * there — resolved once by `App.tsx` and shared, so five screens asking "am I
 * allowed?" is one call for one fact.
 *
 * What it carries is the **evaluation** from `GET /api/v1/me/access` — the
 * same object the API enforces with — never a role. The prototype's
 * `ProdocAccess` had a `role` and a permissions map derived from the org role;
 * neither survives (owner, 2026-10-11: identity is core-auth's, orgs are
 * core-org-management's, permissions are only the evaluate answer). A screen
 * asks `can(resource, verb)` and nothing else.
 */
import { createContext, useContext, type ReactNode } from 'react'

import type { Evaluation, Verb } from '../lib/access'
import type { OrgRow } from '../lib/orgs'
import type { Me } from '../lib/use-me'

export interface OrgState {
  /** The organisation every screen is acting in. */
  org: OrgRow
  /** Every organisation the person belongs to. */
  orgs: OrgRow[]
  /** The evaluate answer, plus the visible workspace ids (plan §4.2). */
  evaluation: Evaluation
  can: (resource: string, verb: Verb) => boolean
  /** The signed-in person — the token's subject, and user-management's display record. */
  userId: string | null
  me: Me | null
  /**
   * Work in another organisation. W22 (FR-59): it comes through the upload
   * provider, which asks first while a file is still uploading and forgets the
   * passwords kept for this tab's pages (D56) before it switches.
   */
  switchOrg: (orgId: string) => void
}

const OrgContext = createContext<OrgState | null>(null)

export function OrgProvider({ value, children }: { value: OrgState; children: ReactNode }) {
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

/** Only valid inside `<App>`, which renders its children once the org and the evaluation are known. */
export function useOrg(): OrgState {
  const value = useContext(OrgContext)
  if (!value) throw new Error('useOrg() must be used inside <App>.')
  return value
}
